import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { User, Mail, Calendar, Clock, Shield, Search, Filter, TrendingUp, Linkedin } from 'lucide-react';
import Layout from '../../components/layout/layout-dashboard';
import { supabase, safeSupabaseQuery } from '../../lib/supabase';
import { useAuth } from '../../AuthProvider';
import Button from '../../components/ui/button';
import toast from 'react-hot-toast';

interface UserData {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: 'admin' | 'client' | 'candidat' | 'editor';
  user_type: string | null;
  created_at: string;
  updated_at: string;
  auth_created_at?: string;
  last_sign_in_at?: string;
  auth_provider?: string | null;
}

interface UserStats {
  total: number;
  admins: number;
  clients: number;
  candidats: number;
  editors: number;
  newThisWeek: number;
  newThisMonth: number;
}

export default function UsersManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserData[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([]);
  const [stats, setStats] = useState<UserStats>({
    total: 0,
    admins: 0,
    clients: 0,
    candidats: 0,
    editors: 0,
    newThisWeek: 0,
    newThisMonth: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [searchTerm, roleFilter, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const { data: usersData, error: usersError, isCorsError: usersCorsError } = await safeSupabaseQuery(
        () => supabase
          .from('users')
          .select('*')
          .order('created_at', { ascending: false })
      );

      if (usersCorsError) {
        toast.error('Erreur de connexion à la base de données');
        setLoading(false);
        return;
      }

      if (usersError) {
        console.error('Error fetching users:', usersError);
        toast.error('Erreur lors du chargement des utilisateurs');
        setLoading(false);
        return;
      }

      setUsers(usersData || []);
      calculateStats(usersData || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (usersData: UserData[]) => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const stats: UserStats = {
      total: usersData.length,
      admins: usersData.filter(u => u.role === 'admin').length,
      clients: usersData.filter(u => u.role === 'client').length,
      candidats: usersData.filter(u => u.role === 'candidat').length,
      editors: usersData.filter(u => u.role === 'editor').length,
      newThisWeek: usersData.filter(u => new Date(u.created_at) > oneWeekAgo).length,
      newThisMonth: usersData.filter(u => new Date(u.created_at) > oneMonthAgo).length,
    };

    setStats(stats);
  };

  const filterUsers = () => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(u =>
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }

    setFilteredUsers(filtered);
  };

  const handleRoleChange = async (userId: string, newRole: 'admin' | 'client' | 'candidat' | 'editor') => {
    try {
      const { error } = await supabase
        .from('users')
        .update({ role: newRole, updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (error) throw error;

      toast.success('Rôle mis à jour avec succès');
      fetchUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Erreur lors de la mise à jour du rôle');
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject || !emailBody) {
      toast.error('Veuillez remplir le sujet et le corps du message');
      return;
    }

    if (selectedUsers.length === 0) {
      toast.error('Veuillez sélectionner au moins un utilisateur');
      return;
    }

    setSendingEmail(true);

    try {
      const usersToEmail = selectedUsers.length > 0
        ? users.filter(u => selectedUsers.includes(u.id))
        : users;

      // Security: emails are PII — do not log them to the console (RGPD).
      console.log(`[email] préparation pour ${usersToEmail.length} destinataire(s)`);

      toast.success(`Email préparé pour ${usersToEmail.length} utilisateur(s)`);

      setEmailSubject('');
      setEmailBody('');
      setSelectedUsers([]);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Erreur lors de l\'envoi des emails');
    } finally {
      setSendingEmail(false);
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'editor':
        return 'bg-blue-100 text-blue-800';
      case 'client':
        return 'bg-green-100 text-green-800';
      case 'candidat':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg text-neutral-600">Chargement...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="container-custom py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-neutral-900 mb-2">Gestion des utilisateurs</h1>
          <p className="text-neutral-600">Gérez les utilisateurs, leurs rôles et envoyez des emails</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-soft p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <User className="text-neutral-600" size={24} />
              <TrendingUp className="text-green-500" size={20} />
            </div>
            <div className="text-3xl font-bold text-neutral-900">{stats.total}</div>
            <div className="text-sm text-neutral-600">Total utilisateurs</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-soft p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <Shield className="text-red-500" size={24} />
            </div>
            <div className="text-3xl font-bold text-neutral-900">{stats.admins}</div>
            <div className="text-sm text-neutral-600">Administrateurs</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-soft p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <Calendar className="text-blue-500" size={24} />
            </div>
            <div className="text-3xl font-bold text-neutral-900">{stats.newThisWeek}</div>
            <div className="text-sm text-neutral-600">Nouveaux cette semaine</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-soft p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-green-500" size={24} />
            </div>
            <div className="text-3xl font-bold text-neutral-900">{stats.newThisMonth}</div>
            <div className="text-sm text-neutral-600">Nouveaux ce mois</div>
          </motion.div>
        </div>

        <div className="bg-white rounded-lg shadow-soft p-6 mb-8">
          <h2 className="text-2xl font-bold text-neutral-900 mb-6">Envoyer un email</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Destinataires ({selectedUsers.length} sélectionné(s))
              </label>
              <p className="text-sm text-neutral-600">
                Sélectionnez les utilisateurs dans la liste ci-dessous
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Sujet</label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d03484]"
                placeholder="Sujet de l'email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Message</label>
              <textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={6}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d03484]"
                placeholder="Corps du message"
              />
            </div>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail || selectedUsers.length === 0}
              variant="gradient"
            >
              {sendingEmail ? 'Envoi en cours...' : `Envoyer à ${selectedUsers.length} utilisateur(s)`}
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-soft p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher par email ou nom..."
                  className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d03484]"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d03484] appearance-none bg-white"
                >
                  <option value="all">Tous les rôles</option>
                  <option value="admin">Admin</option>
                  <option value="editor">Éditeur</option>
                  <option value="client">Client</option>
                  <option value="candidat">Candidat</option>
                </select>
              </div>
              <Button onClick={selectAll} variant="outline">
                {selectedUsers.length === filteredUsers.length ? 'Tout désélectionner' : 'Tout sélectionner'}
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-200">
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={selectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Email</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Nom</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Connexion</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Rôle</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Inscription</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Dernière connexion</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((userData) => (
                  <tr key={userData.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(userData.id)}
                        onChange={() => toggleUserSelection(userData.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Mail size={16} className="text-neutral-400" />
                        <span className="text-sm text-neutral-900">{userData.email}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-neutral-900">
                        {userData.first_name || userData.last_name
                          ? `${userData.first_name || ''} ${userData.last_name || ''}`.trim()
                          : '-'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {userData.auth_provider === 'linkedin_oidc' ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0077b5]/10 px-2.5 py-0.5 text-xs font-semibold text-[#0077b5]">
                          <Linkedin className="w-3 h-3" />
                          LinkedIn
                        </span>
                      ) : userData.auth_provider === 'email' ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-semibold text-neutral-600">
                          <Mail className="w-3 h-3" />
                          Email
                        </span>
                      ) : (
                        <span className="text-xs text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <select
                        value={userData.role}
                        onChange={(e) => handleRoleChange(userData.id, e.target.value as any)}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(userData.role)}`}
                      >
                        <option value="admin">Admin</option>
                        <option value="editor">Éditeur</option>
                        <option value="client">Client</option>
                        <option value="candidat">Candidat</option>
                      </select>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-neutral-600">
                        {formatDate(userData.created_at)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-neutral-600">
                        {formatDate(userData.last_sign_in_at)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleUserSelection(userData.id)}
                      >
                        {selectedUsers.includes(userData.id) ? 'Désélectionner' : 'Sélectionner'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-neutral-600">Aucun utilisateur trouvé</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
